/**
 * TanStack Query client and provider for the Geode web app.
 *
 * SSR-safe:
 * - On the server, a new QueryClient is created per request (no shared state).
 * - On the client, the singleton client is reused and hydrated from server state.
 * - HydrationBoundary wraps children so server-prefetched data is available
 *   immediately on the client without redundant fetches.
 */

import type React from 'react';
import { QueryClient, QueryClientProvider, HydrationBoundary } from '@tanstack/react-query';

const isServerSide = typeof process !== 'undefined' && !!(process as NodeJS.Process).versions?.node;

// Client-side singleton — created once and reused across navigations.
let browserQueryClient: QueryClient | undefined;

function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
        staleTime: 30_000,      // 30 seconds before background refetch
        gcTime: 5 * 60 * 1000,  // 5 minutes garbage collection (formerly cacheTime)
      },
      mutations: {
        retry: 0,
      },
    },
  });
}

export function getQueryClient(): QueryClient {
  if (!isServerSide) {
    // Browser: singleton pattern
    if (!browserQueryClient) {
      browserQueryClient = createQueryClient();
    }
    return browserQueryClient;
  }

  // Server: always create a fresh client per request.
  // This prevents data leaking between requests.
  return createQueryClient();
}

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const queryClient = getQueryClient();
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}
